import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import RecentActivity from '../../components/RecentActivity';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  Users,
  Wallet,
  TrendingUp,
  IndianRupee,
  UserPlus,
  Clock
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const AdminDashboard = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const COLORS = ['#ef785b', '#F97316', '#1A1A1A'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ef785b]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const pieData = [
    { name: 'Affiliate', value: stats?.revenue_by_type?.affiliate || 0 },
    { name: 'Referral', value: stats?.revenue_by_type?.referral || 0 },
    { name: 'Solution', value: stats?.revenue_by_type?.solution || 0 },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">
            Admin Overview
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Monitor partner performance and platform metrics
          </p>
        </div>

        {/* KPI Cards - Row 1 */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ef785b]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#ef785b]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Partners</p>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="total-partners">
                    {stats?.total_partners || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Active</p>
                  <p className="text-xl font-bold text-green-600" data-testid="active-partners">
                    {stats?.active_partners || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Pending</p>
                  <p className="text-xl font-bold text-amber-600" data-testid="pending-partners">
                    {stats?.pending_partners || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Clients</p>
                  <p className="text-xl font-bold text-blue-600" data-testid="total-clients">
                    {stats?.total_clients || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total MRR</p>
                  <p className="text-xl font-bold text-violet-600" data-testid="total-mrr">
                    {formatCurrency(stats?.total_mrr || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Pending Payout</p>
                  <p className="text-xl font-bold text-red-600" data-testid="pending-payout">
                    {formatCurrency(stats?.pending_payouts || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Partner Type Breakdown */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-blue-600 font-medium">Affiliate Partners</p>
              <p className="text-3xl font-bold text-blue-700">{stats?.affiliate_count || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#ef785b]/5 border-[#ef785b]/20">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-[#ef785b] font-medium">Referral Partners</p>
              <p className="text-3xl font-bold text-[#ef785b]">{stats?.referral_count || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-violet-50 border-violet-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-violet-600 font-medium">Solution Partners</p>
              <p className="text-3xl font-bold text-violet-700">{stats?.solution_count || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Partner Growth Chart */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="text-lg">
                Partner Growth
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats?.monthly_partner_growth || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E2DF" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => {
                        const [year, month] = value.split('-');
                        return new Date(year, month - 1).toLocaleString('default', { month: 'short' });
                      }}
                    />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#ef785b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Revenue by Partner Type */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="text-lg">
                Revenue by Partner Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Partners */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardHeader>
            <CardTitle className="text-lg">
              Top 10 Partners by Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(stats?.top_partners || []).map((partner, index) => (
                <div key={partner.id} className="flex items-center gap-4 p-3 bg-[#FAFAF8] rounded-lg" data-testid={`top-partner-${index}`}>
                  <div className="w-8 h-8 rounded-full bg-[#ef785b] flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#1A1A1A]">{partner.name}</p>
                    <p className="text-xs text-[#64748B] capitalize">{partner.type} Partner</p>
                  </div>
                  <p className="font-bold text-[#ef785b]">{formatCurrency(partner.revenue)}</p>
                </div>
              ))}
              {(!stats?.top_partners || stats.top_partners.length === 0) && (
                <p className="text-center text-[#64748B] py-4">No partner data available yet.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <RecentActivity />
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
